import { useCallback, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { TransformPackage, TransformResponse, JobStatus } from '../@types/Templates';
import { useAxios } from '../context/AxiosContext';
import { useAuth } from '../context/AuthContext';
import { AxiosError } from 'axios';
import { downloadFile } from '../utils/fileDownload';
import moment from 'moment';

export function useJobDetails(id: string) {
  const { authAxios } = useAxios();

  const fetchJobDetails = useCallback(
    async (id) => {
      const response = await authAxios.get<TransformPackage>(`/reseller/jobs/${id}/details`);
      return response.data;
    },
    [authAxios]
  );

  return useQuery(['job-details', id], () => fetchJobDetails(id), { refetchOnWindowFocus: false, suspense: true });
}

export function useSaveAndReprocess(id: string) {
  const { authAxios } = useAxios();

  const saveAndReprocess = useCallback(
    async (template) => {
      const response = await authAxios.post(`/reseller/jobs/reprocess/${id}`, {
        template: template
      });
      return response.data;
    },
    [authAxios, id]
  );
  const saveAndReprocessMutation = useMutation(saveAndReprocess, {
    onError: () => {
      toast.error('Error', { position: 'top-center' });
    }
  });

  return { saveAndReprocess: saveAndReprocessMutation.mutateAsync };
}

export function useJobRefresh(id: string) {
  const { authAxios } = useAxios();

  const refreshData = useCallback(
    async (template) => {
      const response = await authAxios.post<TransformPackage>(`/reseller/jobs/${id}/refresh`, { template });
      return response.data;
    },
    [authAxios, id]
  );

  const refreshMutation = useMutation(refreshData, {
    onError: () => {
      toast.error('Error occurred processing files', { position: 'top-center' });
    }
  });

  return {
    refresh: refreshMutation.mutateAsync,
    isLoading: refreshMutation.isLoading
  };
}

export function useJobStatus(id: string) {
  const { authAxios } = useAxios();

  const updateJobStatusMutation = useMutation(
    (status: JobStatus) => authAxios.put(`/reseller/job/${id}/${status}`, { status }),
    {
      onSuccess: (_, status) => {
        toast.success(`Job status is now ${status}`, { position: 'top-center' });
      },
      onError: (error: AxiosError) => {
        toast.success(toast.error(error.message, { position: 'top-center' }));
      }
    }
  );

  return {
    updateJobStatus: updateJobStatusMutation.mutateAsync
  };
}

export function useStopJob() {
  const { authAxios } = useAxios();

  const stopJobMutation = useMutation(
    async ({ _id }: Record<string, any>) => {
      await authAxios.put(`/reseller/job/${_id}/${JobStatus.Stopped}`);
    },
    {
      onSuccess: (_, { ZipFileName, CreatedAt }) => {
        toast.success(`${ZipFileName} ${moment(CreatedAt).format('MM/DD/YYYY hh:mm a')} job was stopped successfully`, {
          position: 'top-center'
        });
      },
      onError: (_: AxiosError, { ZipFileName, CreatedAt }) => {
        toast.error(`${ZipFileName} ${moment(CreatedAt).format('MM/DD/YYYY hh:mm a')} job could not be stopped`, {
          position: 'top-center'
        });
      }
    }
  );

  return {
    stopJob: stopJobMutation.mutateAsync
  };
}

export function useHandleJobFiles() {
  const { authAxios } = useAxios();
  const {
    authState: { userInfo }
  } = useAuth();
  const path = '/reseller';
  const [auditId, setAuditId] = useState<string>('');
  const onJobUpload = useCallback(
    async (file: File): Promise<void> => {
      const requestBody = new FormData();
      requestBody.append('file', file);
      requestBody.append('client', userInfo?.email || '');
      requestBody.append('auditId', auditId);

      await authAxios.post<TransformResponse>(`${path}/job`, requestBody);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authAxios, auditId]
  );

  const uploadJobMutation = useMutation(onJobUpload, {
    onSuccess: () => {
      toast.success('File successfully uploaded!', { position: 'top-center' });
    }
  });

  const onZipDownload = async ({ id, fileName }: { id: string; fileName: string }): Promise<void> => {
    const response = await authAxios.get<TransformResponse>(`${path}/job/${id}/zip`, { responseType: 'blob' });
    downloadFile(response.data, fileName);
  };

  const onZipDownloadMutation = useMutation(onZipDownload);

  const onErrorsDownload = async (rowId: string): Promise<void> => {
    const response = await authAxios.get<TransformResponse>(`${path}/jobs/${rowId}/validationErrors`);
    downloadFile(response.data, `${rowId}.csv`);
  };

  const onErrorsDownloadMutation = useMutation(onErrorsDownload);

  return {
    setAuditId,
    onJobUpload: uploadJobMutation.mutateAsync,
    onZipDownload: onZipDownloadMutation.mutateAsync,
    onErrorsDownload: onErrorsDownloadMutation.mutateAsync
  };
}
